/*
 * Copyright (c) Nexign, JSC, 2023-2024.
 */
/**
 *
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.25.1
 */

import megafonIcon from '@megafon/ui-icons/service-logos-24-megafon_lk.svg';
import isFunction from 'lodash/isFunction';

import {
    CLIENT_ID,
    ROUTE_INQUIRY_ID,
    ROUTE_NEWS_ID,
    ROUTE_ORDER_ID,
    RoutePath,
} from '@/main/routes/constants/routesConstants';

import { IUserNotificationTranslation } from '../../internationalization/types';
import { ENotificationsItemType, GROUP_OVERFLOW_LIMIT, NOTIFICATIONS_FILTRED_INQUIRIES_LINK } from '../constants';
import { INotificationsEntity, INotificationsItem } from '../types';
import { getNotificationApiTitle, getNotificationContent } from './commonNotificationsUtils';
import { getEdmNotificationContent } from './getEdmNotificationData';
import { getInquiriesNotificationContent } from './getInquiriesNotificationData';
import { getOrdersNotificationContent } from './getOrdersNotificationData';

export function getIsNotificationsSupported() {
    return 'Notification' in window;
}

export async function askUserPermissionForNotifications() {
    return await Notification.requestPermission();
}

export function getIsGroupNotification(entity: INotificationsEntity) {
    return !!(entity.count && entity.count > 1);
}

export function getIsOverflowNotification(entity: INotificationsEntity) {
    return !!(entity.count && entity.count > GROUP_OVERFLOW_LIMIT);
}

export function getIsExternalNotification(entity: INotificationsEntity) {
    return !!entity.link;
}

export function getOrderNotificationURL(orderId?: string | number) {
    return RoutePath.orderId.replace(ROUTE_ORDER_ID, String(orderId));
}
export function getInquiryNotificationURL(inquiryId?: string | number) {
    return RoutePath.inquiryInfo.replace(ROUTE_INQUIRY_ID, String(inquiryId));
}
export function getEdmNotificationURL(clientId?: string | number) {
    return RoutePath.accountInfoAccs.replace(CLIENT_ID, String(clientId));
}

export function getNewsNotificationURL(newsId?: string | number) {
    return RoutePath.newsId.replace(ROUTE_NEWS_ID, String(newsId));
}

export function createNewNotification(
    item: INotificationsItem,
    translation: IUserNotificationTranslation,
    isServiceUser: boolean,
    options?: {
        handleExternalNewsViewed?: (id: number | string) => void;
        handleCvmNotificationViewed?: (id: number | string) => void;
        handleCashbackNotificationViewed?: (id: number | string) => void;
    },
) {
    const notificationData = {
        title: '',
        onClick: () => {},
    };
    switch (item.type) {
        case ENotificationsItemType.NOTIFICATION_CVM:
            notificationData.title = getNotificationApiTitle(item.entity);
            notificationData.onClick = () => {
                if (
                    !isServiceUser &&
                    item.entity.link &&
                    item.entity.id &&
                    options &&
                    isFunction(options.handleCvmNotificationViewed)
                ) {
                    options.handleCvmNotificationViewed(item.entity.id);
                }
                handleCvmNotificationClick(item.entity.link);
            };
            break;
        case ENotificationsItemType.NOTIFICATION_NEWS:
            const isExternalNews = getIsExternalNotification(item.entity);
            notificationData.title = getNotificationContent(item.entity);
            notificationData.onClick = () => {
                if (isExternalNews && item.entity.id && options && isFunction(options.handleExternalNewsViewed)) {
                    options.handleExternalNewsViewed(item.entity.id);
                }
                handleNotificationClick(isExternalNews ? item.entity.link : getNewsNotificationURL(item.entity.id));
            };
            break;
        case ENotificationsItemType.NOTIFICATION_EDM:
            notificationData.title = getEdmNotificationContent(item.entity, translation);

            notificationData.onClick = () =>
                item.entity.id ? handleNotificationClick(getEdmNotificationURL(item.entity.id)) : null;

            break;
        case ENotificationsItemType.NOTIFICATION_ORDERS:
            const isGroupedOrders = getIsGroupNotification(item.entity);
            const isOverflowedOrders = getIsOverflowNotification(item.entity);
            notificationData.title = getOrdersNotificationContent(item.entity, translation, {
                isGroup: isGroupedOrders,
                isOverflow: isOverflowedOrders,
            });
            if (isGroupedOrders) {
                notificationData.onClick = () => handleNotificationClick(RoutePath.orders);
            } else {
                notificationData.onClick = () =>
                    item.entity.id ? handleNotificationClick(getOrderNotificationURL(item.entity.id)) : null;
            }
            break;
        case ENotificationsItemType.NOTIFICATION_INQUIRIES:
            const isGroupedInquiries = getIsGroupNotification(item.entity);
            const isOverflowedInquiries = getIsOverflowNotification(item.entity);

            notificationData.title = getInquiriesNotificationContent(item.entity, translation, {
                isGroup: isGroupedInquiries,
                isOverflow: isOverflowedInquiries,
            }) as string;

            if (isGroupedInquiries) {
                notificationData.onClick = () =>
                    isOverflowedInquiries
                        ? handleNotificationClick(RoutePath.inquiries)
                        : handleNotificationClick(NOTIFICATIONS_FILTRED_INQUIRIES_LINK);
            } else {
                notificationData.onClick = () =>
                    item.entity.id ? handleNotificationClick(getInquiryNotificationURL(item.entity.id)) : null;
            }
            break;
		case ENotificationsItemType.NOTIFICATION_CASHBACK:
            notificationData.title = getNotificationApiTitle(item.entity);
            notificationData.onClick = () => {
                if (item.entity.link) {
                    if (!isServiceUser && options && isFunction(options.handleCashbackNotificationViewed)) {
                        options.handleCashbackNotificationViewed(item.id);
                    }
                    handleNotificationClick(item.entity.link);
                }
            };
            break;
        default:
            undefined;
    }
    if (notificationData.title) {
        const notification = new Notification(notificationData.title, { icon: megafonIcon });
        notification.onclick = notificationData.onClick;
        return notification;
    }
}

const handleNotificationClick = (url?: string | URL) => {
    if (!url) return;
    window.open(url)?.focus();
};

const handleCvmNotificationClick = (url?: string | URL) => {
    if (!url) return;
    window.open(url, '_blank')?.focus();
};
